/**
 * 直播间主区域舞台类型
 */

import {Content} from "../api/content-api";
import {DrawObject} from "../draw-board";
import {RoomCounter, RoomMode, RoomState, RoomUser, RoomVirtualUser, ScreenMode} from "../room-engine/types";

export enum RoomStageType {

    /**
     * 无
     */
    NONE = "none",

    /**
     * 文档
     */
    DOCUMENT = "doc",

    /**
     * 白板
     */
    BLANK = "blank",

    /**
     * 摄像头
     */
    CAMERA = "camera",

    /**
     * 屏幕分享
     */
    SCREEN = "screen",

    /**
     * OBS
     */
    OBS = "obs",

    /**
     * 录播直播
     */
    PSEUDO = "pseudo",
}

export enum StreamPosition {

    VIDEO_CALL = "video_call",

    CAMERA_SD = "camera_sd",

    CAMERA_HD = "camera_hd",

    SCREEN = "screen",

    OBS = "obs",

    FILM = "film",
}

export enum StreamPushStatus {
    /**
     * 未推流
     */
    NONE = "none",

    /**
     * 推流中
     */
    PUSHING = "pushing",

    /**
     * 推流已结束
     */
    CLOSED = "closed",
}

export interface CanvasObjectUrl {
    contentId: number;

    page: number;

    url: string;

    lastDrawAt: number;
}

export interface CanvasPage {
    lastDrawAt: number;

    contentId: number;

    page: number;

    objects: Array<DrawObject>;
}

export interface CanvasThumbs {
    prefix: string;

    pages: Array<number>;
}

export interface StreamStage {
    streamId: number;

    streamHlsPlayUrl: string;

    streamFlvPlayUrl: string;
}

export interface ContentStage extends StreamStage {
    contentId: number;

    /**
     * 当前页码
     */
    currentPage: number;

    /**
     * ppt动画 步骤
     */
    currentStep?: number;

    /**
     * 画布缩略图
     */
    canvasThumbs?: CanvasThumbs;

    content?: Content;
}



export interface Stream {
    id: number;

    /**
     * 教师端：推流状态 ('none', 'pushing', 'closed')
     */
    pushStatus: string;

    rtcNetworkQualityLevel: number;

    rtcRtt: string;

    rtcPacketsLostRate: string;

    rtcBitrate: string;

    talkStreamToken: string;
}

export interface OBSState {
    streamId: number;

    pushServer: string;

    pushKey: string;

    hlsPlayUrl: string;

    flvPlayUrl: string;
}

export type PlayerState = "none" | "playing" | "waiting" | "failed";

/**
 * 通过SDK配置的浮动按钮
 */
export interface SDKFloatButton {
    code: string;
    icon?: string;
    name?: string;
    [key: string]: unknown;
}

/**
 * 通过SDK配置的Tab
 */
export interface SDKTab {
    code: string;
    name: string;
    [key: string]: unknown;
}

export interface Trailer {
    image?: string;
    video?: string;
}

export type DeviceSetting = Pick<RoomStoreState, "micVolume" | "micDeviceId" | "cameraDeviceId" | "spkDeviceId">;

export interface RoomStoreState {

    /**
     * 是否为直播
     */
    isLive: boolean;

    /**
     * 是否为回放
     */
    isReplay: boolean;

    /**
     * 侧边栏是否显示
     */
    sideOpened: boolean;

    /**
     * 课堂ID
     */
    id: number;

    /**
     * 直播类型
     */
    type: string;

    /**
     * 直播间名称
     */
    name: string;

    /**
     * 直播间当前状态
     */
    status: RoomState;

    /**
     * 直播模式
     */
    mode: RoomMode;

    /**
     * 录播直播视频地址
     */
    pseudoVideoUrl: string;

    /**
     * 直播预设开始时间
     */
    startAt: number;

    /**
     * 直播预设结束时间
     */
    endAt: number;

    /**
     * 直播实际开始时间
     */
    actualStartAt: number;

    /**
     * 直播实际结束时间
     */
    actualEndAt: number;

    /**
     * 直播剩余时间
     */
    remainTime: number;

    /**
     * 是否有过延时
     */
    hasOvertime: boolean;

    /**
     * 连麦限制人数
     */
    talkerLimit: number;

    /**
     * 是否在连麦中
     */
    isTalking: boolean;

    /**
     * 录制是否开启
     */
    recordEnable: boolean;

    /**
     * 录制状态
     */
    recordStatus: string;

    /**
     * 网校是否开通回放
     */
    replayEnable: boolean;

    /**
     * logo
     */
    logo: string;

    /**
     * 全员禁言(none, on, off)
     */
    allMute: string;

    /**
     * 网校默认全员禁言
     */
    globalChatMute: boolean;

    /**
     * 强制聊天消息只看老师
     */
    forceChatOnly: boolean;

    /**
     * 是否显示聊天区快速统计
     */
    showComboStats: boolean;

    /**
     * 当前用户
     */
    user: RoomUser;

    counter: RoomCounter;

    virtualUser: RoomVirtualUser;

    /**
     * 是否全屏
     */
    fullscreen: boolean;

    /**
     * 是否拖动状态
     */
    dragEnable: boolean;

    /**
     * 画笔数据
     */
    canvasData: Array<CanvasPage>;

    /**
     * 摄像头设备ID
     */
    cameraDeviceId: string;

    /**
     * 麦克风设备ID
     */
    micDeviceId: string;

    deviceReady: boolean;

    /**
     * 麦克风音量设置
     */
    micVolume: number;

    /**
     * 当前音量大小(0: 没有声音 / 100：声音很大)
     */
    volumeMeter: number;

    /**
     * 扬声器设备ID
     */
    spkDeviceId: string;

    /**
     * 是否开启弹幕
     */
    danmakuEnable: boolean;

    /**
     * 是否只看讲师聊天信息
     */
    chatFiltered: boolean;

    /**
     * 学生端是否显示摄像头
     */
    cameraVisible: boolean;

    /**
     * 引流二维码
     */
    leadQrcode: boolean;

    leadQrcodeUrl: string;

    /**
     * 分享海报
     */
    sharePosterUrl: string;

    /**
     * finish跳转
     */
    finishCallbackUrl: string;

    fileDomain: string;

    /**
     * 快捷键是否可用
     */
    hotKeyEnable: boolean;

    /**
     * 屏幕模式
     */
    screenMode: ScreenMode;

    /**
     * 教师的课件页码
     * (学生分页不能超过此页码)
     */
    speakerPage: number;

    /**
     * 已上传的文档列表
     */
    contents: Array<Content>;

    stream: Stream;

    /**
     * 文档上传对话框的可见性
     */
    uploadModalVisible: boolean;

    /**
     * 文档缩略图
     */
    docThumbnails: Array<string>;
    /**
     * 白板缩略图
     */
    blankThumbnails: Array<string>;

    /**
     * obs状态
     */
    obs: OBSState;

    /**
     * 流播放器状态
     */
    playerState: PlayerState;

    /**
     * 是否显示求助浮层
     */
    showHelpPopup: boolean;

    nativeBridgeConnected: boolean;

    /**
     * 原生键盘高度
     */
    nativeKeyboardHeight: number;

    /**
     * 是否显示 HTML Video Element
     */
    videoElementVisible: boolean;

    /**
     * 通过SDK配置的浮动按钮
     */
    sdkFloatButtons: SDKFloatButton[];

    /**
     * 通过SDK配置的Tab
     */
    sdkTabs: SDKTab[];

    /**
     * 预告片
     */
    trailer: Trailer;

    /**
     * 用户点击直接购买商品的编号
     */
    sdkBuyGoodsNo: string;

    // eslint-disable-next-line
    historyChats: any[];
}

export interface VideoCaller {
    streamId: string;
    userId: number;
    username: string;
    role: string;
}

export interface VideoCallApplier {
    userId: number;
    username: string;
}
